import { inboxClient } from "../../inbox/api/client";
import { instance as boompayClient } from "api/apiBase";
import type {
  Mailbox,
  UpdateMailboxRequest,
  PhoneNumber,
  PhoneNumbersListResponse,
  UpdatePhoneNumberRequest,
  RequestPhoneNumberRequest,
  SearchAvailableParams,
  AvailablePhoneNumber,
  PurchasePhoneNumberRequest,
  PropertyPhoneDefault
} from "./types";

export const mailboxesApi = {
  list: async () => {
    const { data } = await inboxClient.get<Mailbox[]>("/api/portal/mailboxes");
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<Mailbox>(
      `/api/portal/mailboxes/${id}`
    );
    return data;
  },

  update: async (id: string, payload: UpdateMailboxRequest) => {
    const { data } = await boompayClient.patch<Mailbox>(
      `/portal/v1/communication_mailboxes/${id}`,
      payload
    );
    return data;
  },

  setDefault: async (id: string) => {
    await boompayClient.put(`/portal/v1/communication_mailboxes/${id}/default`);
  },

  removeDefault: async () => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/default`);
  },

  setPropertyDefault: async (propertyId: string, mailboxId: string) => {
    await boompayClient.put(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`,
      { mailbox_id: mailboxId }
    );
  },

  removePropertyDefault: async (propertyId: string) => {
    await boompayClient.delete(
      `/portal/v1/communication_mailboxes/properties/${propertyId}/default`
    );
  },

  disconnect: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}`);
  },

  subscribe: async (id: string) => {
    await boompayClient.post(`/portal/v1/communication_mailboxes/${id}/sync`);
  },

  unsubscribe: async (id: string) => {
    await boompayClient.delete(`/portal/v1/communication_mailboxes/${id}/sync`);
  }
};

const PHONE_BASE = "/api/portal/provisioned_phone_numbers";

export const phoneNumbersApi = {
  list: async () => {
    const { data } =
      await inboxClient.get<PhoneNumbersListResponse>(PHONE_BASE);
    return data;
  },

  get: async (id: string) => {
    const { data } = await inboxClient.get<PhoneNumber>(`${PHONE_BASE}/${id}`);
    return data;
  },

  searchAvailable: async (params: SearchAvailableParams) => {
    const { data } = await inboxClient.get<AvailablePhoneNumber[]>(
      `${PHONE_BASE}/available`,
      { params }
    );
    return data;
  },

  purchase: async (payload: PurchasePhoneNumberRequest) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${PHONE_BASE}/purchase`,
      payload
    );
    return data;
  },

  request: async (payload?: RequestPhoneNumberRequest) => {
    const { data } = await inboxClient.post<PhoneNumber>(
      `${PHONE_BASE}/request`,
      payload
    );
    return data;
  },

  setDefault: async (id: string) => {
    const { data } = await inboxClient.put<PhoneNumber>(
      `${PHONE_BASE}/${id}/default`
    );
    return data;
  },

  update: async (id: string, payload: UpdatePhoneNumberRequest) => {
    const { data } = await inboxClient.patch<PhoneNumber>(
      `${PHONE_BASE}/${id}`,
      payload
    );
    return data;
  },

  remove: async (id: string) => {
    await inboxClient.delete(`${PHONE_BASE}/${id}`);
  },

  listPropertyDefaults: async () => {
    const { data } = await inboxClient.get<PropertyPhoneDefault[]>(
      `${PHONE_BASE}/properties/defaults`
    );
    return data;
  },

  getPropertyDefault: async (propertyId: number) => {
    const { data } = await inboxClient.get<PropertyPhoneDefault | null>(
      `${PHONE_BASE}/properties/${propertyId}/default`
    );
    return data;
  },

  setPropertyDefault: async (propertyId: number, phoneNumberId: string) => {
    const { data } = await inboxClient.put(
      `${PHONE_BASE}/properties/${propertyId}/default`,
      { phone_number_id: phoneNumberId }
    );
    return data;
  },

  removePropertyDefault: async (propertyId: number) => {
    await inboxClient.delete(`${PHONE_BASE}/properties/${propertyId}/default`);
  }
};
